import React from "react";

import cx from "classnames";

import { coalesce } from "@kazoohr/helpers";

import { useBlurHandlers } from "../../helpers/useBlurHandlers";
import { EmployeeList } from "../EmployeeList";
import { Employee } from "../EmployeeList/EmployeeList";
import { Icon } from "../Icon";
import { IconDictionary } from "../Icon/types";
import { Popover } from "../Popover";
import { ProfilePic } from "../ProfilePic";

import styles from "./Pill.scss";

export type PillVariants =
  | "gray"
  | "outline"
  | "primary"
  | "secondary"
  | "warning"
  | "counter";
export type PillSize = "large" | "small";
export type PillElement = string | number | React.ReactNode | React.ReactNode[];

export interface PillProps {
  /**
   * Icon to display in the pill
   */
  icon?: IconDictionary;
  /**
   * Text content of the pill
   */
  text?: PillElement;
  /**
   * Content to show on hover
   */
  hoverContent?: PillElement;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Test ID for the pill
   */
  "data-testid"?: string;
  /**
   * Visual variant of the pill
   */
  variant?: PillVariants;
  /**
   * Size of the pill
   */
  size?: PillSize;
  /**
   * Whether the pill shows a list on click
   */
  listShowable?: boolean;
  /**
   * List of users to show when listShowable is true
   */
  users?: Employee[];
  /**
   * Click handler for the pill
   */
  onClick?: () => void;
  /**
   * Close handler for the pill
   */
  onClose?: () => void;
  /**
   * Key press handler for the pill
   */
  onKeyPress?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  /**
   * User data for avatar display
   */
  avatarUser?: Record<string, unknown>;
  /**
   * Tab index for the pill
   */
  tabIndex?: number;
  /**
   * Mouse enter handler for the pill
   */
  onMouseEnter?: () => void;
  /**
   * Mouse leave handler for the pill
   */
  onMouseLeave?: () => void;
}

/**
 * A pill component to hold small tidbits of text and even an icon.
 */
// eslint-disable-next-line max-lines-per-function
export function Pill({
  icon,
  className = "",
  variant = "gray",
  size = "large",
  text,
  listShowable,
  hoverContent,
  users = [],
  onClose,
  onClick,
  avatarUser,
  tabIndex,
  onKeyPress,
  onMouseEnter,
  onMouseLeave,
  ...extra
}: PillProps) {
  const [isHovering, setIsHovering] = React.useState(false);
  const dataTestId = extra["data-testid"];
  const variantClassName = `pill--${variant}`;
  const hasHover = Boolean(listShowable || onClick);
  const brandingStyle = coalesce(styles[variantClassName], variantClassName);
  const hoverStyle = coalesce(
    styles[`${variantClassName}-has-hover`],
    `${variantClassName}-has-hover`
  );

  const pillStyles = cx(className, {
    [styles["pill"]]: true,
    [brandingStyle]: true,
    [styles[size]]: true,
    [styles["has-hover"]]: hasHover,
    [`${variantClassName}-has-hover`]: hasHover,
    [hoverStyle]: hasHover,
  });

  const excludeBackground = variant === "primary" || variant === "secondary";

  const iconStyle = excludeBackground
    ? `${variantClassName}-color`
    : variantClassName;
  const [focused, setFocused] = React.useState(false);

  const [blurHandler, childFocusHandler] = useBlurHandlers(() => {
    setFocused(false);
    setClicked(false);
  });

  const focusHandler = () => {
    setFocused(true);
    childFocusHandler();
  };

  const [clicked, setClicked] = React.useState(false);

  const onKeyDown = (evt: React.KeyboardEvent<HTMLDivElement>) => {
    if (evt.key === "Enter") {
      setClicked(!clicked);
    }
  };

  let containerProps: Record<string, unknown> = {
    onClick,
  };

  const mouseEnterHandler = () => {
    setIsHovering(true);
    onMouseEnter?.();
  };
  const mouseLeaveHandler = () => {
    setIsHovering(false);
    onMouseLeave?.();
  };

  if (listShowable) {
    containerProps = {
      role: "button",
      tabIndex: 0,
      onClick: () => setClicked(!clicked),
      onKeyDown: onKeyDown,
      onFocus: focusHandler,
      onBlur: blurHandler,
    };
  }

  const PillContent = (
    <div
      role={onClick ? "button" : ""}
      tabIndex={tabIndex}
      onKeyPress={onKeyPress}
      data-testid={dataTestId}
      className={pillStyles}
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
      {...containerProps}
    >
      {avatarUser && (
        <div data-testid="pill-avatar" className={styles["pill-avatar"]}>
          <ProfilePic dimensions={24} size="small" user={avatarUser} />
        </div>
      )}
      {icon && (
        <div
          data-testid={`${coalesce(dataTestId, "pill")}-icon-wrapper`}
          className={styles.icon}
        >
          <Icon icon={icon} size={12} className={iconStyle} variant="thin" />
        </div>
      )}
      <div data-testid="pill-text">{text}</div>
      {onClose && (
        <div data-testid="pill-close-icon" className={styles["pill-close"]}>
          <div
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <Icon className={iconStyle} size={8} icon="close" variant="thin" />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {Boolean(listShowable) && (
        <Popover
          parent={PillContent}
          isOpen={clicked && focused}
          popoverComponent={<EmployeeList employees={users} />}
          position="bottom-start"
          className={styles["list-card"]}
        />
      )}
      {Boolean(hoverContent) && (
        <Popover
          parent={PillContent}
          isOpen={isHovering}
          popoverComponent={hoverContent}
          position="top-start"
          className={styles["pill-hover-content"]}
        />
      )}
      {!listShowable && !hoverContent && PillContent}
    </>
  );
}
